import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, TrendingUp, Users, DollarSign, Star, ArrowUp, ArrowDown, Eye, Activity } from 'lucide-react';

const VendorsAnalytics: React.FC = () => {
  const [hoveredMetric, setHoveredMetric] = useState<number | null>(null);
  const [selectedChart, setSelectedChart] = useState<'revenue' | 'orders' | 'vendors'>('revenue');
  const [animatedValues, setAnimatedValues] = useState({
    orders: 0,
    vendors: 0,
    revenue: 0,
    satisfaction: 0
  });

  // Animated counter effect
  useEffect(() => {
    const targets = { orders: 2.5, vendors: 15, revenue: 180, satisfaction: 4.8 };
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      setAnimatedValues({
        orders: Math.min(targets.orders * easeOutQuart, targets.orders),
        vendors: Math.min(targets.vendors * easeOutQuart, targets.vendors),
        revenue: Math.min(targets.revenue * easeOutQuart, targets.revenue),
        satisfaction: Math.min(targets.satisfaction * easeOutQuart, targets.satisfaction)
      });

      if (currentStep >= steps) {
        clearInterval(interval);
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, []);

  const businessMetrics = [
    { 
      label: "Total Orders", 
      value: `${animatedValues.orders.toFixed(1)}M+`, 
      growth: "+45%", 
      icon: BarChart3, 
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      description: "Monthly order volume across all vendors"
    },
    { 
      label: "Active Vendors", 
      value: `${animatedValues.vendors.toFixed(0)}K+`, 
      growth: "+30%", 
      icon: Users, 
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      description: "Verified vendors actively selling on platform"
    },
    { 
      label: "Platform Revenue", 
      value: `₹${animatedValues.revenue.toFixed(0)}Cr`, 
      growth: "+60%", 
      icon: DollarSign, 
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      description: "Total gross merchandise value processed"
    },
    { 
      label: "Vendor Satisfaction", 
      value: `${animatedValues.satisfaction.toFixed(1)}★`, 
      growth: "+12%", 
      icon: Star, 
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      description: "Average vendor rating and satisfaction score"
    }
  ];

  const chartData = {
    revenue: [
      { month: "Jan", value: 12, label: "₹12Cr" },
      { month: "Feb", value: 15, label: "₹15Cr" },
      { month: "Mar", value: 18, label: "₹18Cr" },
      { month: "Apr", value: 22, label: "₹22Cr" },
      { month: "May", value: 28, label: "₹28Cr" },
      { month: "Jun", value: 35, label: "₹35Cr" },
      { month: "Jul", value: 42, label: "₹42Cr" },
      { month: "Aug", value: 48, label: "₹48Cr" },
      { month: "Sep", value: 55, label: "₹55Cr" },
      { month: "Oct", value: 62, label: "₹62Cr" },
      { month: "Nov", value: 70, label: "₹70Cr" },
      { month: "Dec", value: 80, label: "₹80Cr" }
    ],
    orders: [
      { month: "Jan", value: 180, label: "180K" },
      { month: "Feb", value: 220, label: "220K" },
      { month: "Mar", value: 280, label: "280K" },
      { month: "Apr", value: 320, label: "320K" },
      { month: "May", value: 380, label: "380K" },
      { month: "Jun", value: 420, label: "420K" },
      { month: "Jul", value: 480, label: "480K" },
      { month: "Aug", value: 520, label: "520K" },
      { month: "Sep", value: 580, label: "580K" },
      { month: "Oct", value: 620, label: "620K" },
      { month: "Nov", value: 680, label: "680K" },
      { month: "Dec", value: 750, label: "750K" }
    ],
    vendors: [
      { month: "Jan", value: 8, label: "8K" },
      { month: "Feb", value: 10, label: "10K" },
      { month: "Mar", value: 12, label: "12K" },
      { month: "Apr", value: 14, label: "14K" },
      { month: "May", value: 16, label: "16K" },
      { month: "Jun", value: 18, label: "18K" },
      { month: "Jul", value: 22, label: "22K" },
      { month: "Aug", value: 25, label: "25K" },
      { month: "Sep", value: 28, label: "28K" },
      { month: "Oct", value: 32, label: "32K" },
      { month: "Nov", value: 35, label: "35K" },
      { month: "Dec", value: 40, label: "40K" }
    ]
  };

  const detailedMetrics = [
    { 
      category: "📊 Order Analytics", 
      icon: Activity,
      data: [
        { label: "Daily Orders", value: "14K+", trend: "+35%", isPositive: true },
        { label: "Peak Hour Volume", value: "2.8K", trend: "+28%", isPositive: true },
        { label: "Average Order Value", value: "₹285", trend: "+18%", isPositive: true },
        { label: "Success Rate", value: "98.2%", trend: "+5%", isPositive: true }
      ]
    },
    { 
      category: "🚀 Vendor Growth", 
      icon: TrendingUp,
      data: [
        { label: "New Vendors/Week", value: "210+", trend: "+42%", isPositive: true },
        { label: "Retention Rate", value: "94%", trend: "+8%", isPositive: true },
        { label: "Product Categories", value: "50+", trend: "+25%", isPositive: true },
        { label: "Cities Covered", value: "45+", trend: "+67%", isPositive: true }
      ]
    },
    { 
      category: "💰 Financial Health", 
      icon: DollarSign,
      data: [
        { label: "Monthly GMV", value: "₹50Cr", trend: "+55%", isPositive: true },
        { label: "Vendor Earnings", value: "₹35Cr", trend: "+62%", isPositive: true },
        { label: "Commission Rate", value: "8%", trend: "-15%", isPositive: false },
        { label: "Payout Speed", value: "24hrs", trend: "0%", isPositive: null }
      ]
    },
    { 
      category: "⭐ Experience Metrics", 
      icon: Eye,
      data: [
        { label: "App Rating", value: "4.8★", trend: "+12%", isPositive: true },
        { label: "Delivery Success", value: "99.1%", trend: "+3%", isPositive: true },
        { label: "Support Rating", value: "4.9★", trend: "+8%", isPositive: true },
        { label: "Repeat Orders", value: "78%", trend: "+22%", isPositive: true }
      ]
    }
  ];

  const maxValue = Math.max(...chartData[selectedChart].map(d => d.value));

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-blue-200/30 to-purple-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-green-200/30 to-yellow-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-red-200/20 to-orange-200/20 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Enhanced Section Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full px-6 py-3 mb-8 shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
          >
            <BarChart3 size={24} className="animate-pulse" />
            <span className="font-semibold">Live Business Intelligence</span>
          </motion.div>
          
          <h2 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
            Business <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-red-500 bg-clip-text text-transparent">Dynamics</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Real-time insights into our platform's performance, vendor success metrics, and market dynamics
          </p>
        </motion.div>

        {/* Enhanced Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {businessMetrics.map((metric, index) => (
            <motion.div
              key={index}
              className="group relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              onHoverStart={() => setHoveredMetric(index)}
              onHoverEnd={() => setHoveredMetric(null)}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${metric.bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Floating Icon Background */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                <metric.icon size={80} />
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div className={`p-4 rounded-2xl bg-gradient-to-r ${metric.color} text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <metric.icon size={32} />
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-bold px-3 py-2 rounded-full ${
                    metric.growth.includes('+') ? 'bg-green-100 text-green-700' : 
                    metric.growth.includes('-') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                  } group-hover:scale-105 transition-transform duration-300`}>
                    {metric.growth.includes('+') ? <ArrowUp size={16} /> : metric.growth.includes('-') ? <ArrowDown size={16} /> : null}
                    {metric.growth}
                  </div>
                </div>
                
                <div className="text-4xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors duration-300">
                  {metric.value}
                </div>
                <div className="text-lg font-semibold text-gray-700 mb-2 group-hover:text-gray-800 transition-colors duration-300">
                  {metric.label}
                </div>
                
                <AnimatePresence>
                  {hoveredMetric === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200"
                    >
                      {metric.description}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Interactive Chart Section */}
        <motion.div 
          className="bg-white rounded-3xl p-10 shadow-2xl border border-gray-100 mb-20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-10">
            <div>
              <h3 className="text-3xl font-bold text-gray-800 mb-3">Growth Analytics Dashboard</h3>
              <p className="text-gray-600 text-lg">Interactive visualization of key performance metrics</p>
            </div>
            
            {/* Chart Type Selector */}
            <div className="flex items-center gap-2 mt-6 lg:mt-0">
              {[
                { key: 'revenue', label: 'Revenue', color: 'from-red-500 to-orange-500' },
                { key: 'orders', label: 'Orders', color: 'from-blue-500 to-purple-500' },
                { key: 'vendors', label: 'Vendors', color: 'from-green-500 to-teal-500' }
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => setSelectedChart(option.key as any)}
                  className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                    selectedChart === option.key
                      ? `bg-gradient-to-r ${option.color} text-white shadow-lg scale-105`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Enhanced Chart */}
          <div className="relative h-96 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 overflow-hidden">
            {/* Chart Container */}
            <div className="h-full w-full relative">
              {/* Chart Area - Fixed height for bars */}
              <div className="absolute bottom-16 left-0 right-0 h-64 flex items-end justify-between gap-2">
                {chartData[selectedChart].map((data, index) => {
                  const barHeight = Math.max((data.value / maxValue) * 256, 8); // Minimum 8px height
                  
                  return (
                    <motion.div
                      key={`${selectedChart}-${index}`}
                      className="flex flex-col items-center flex-1 group cursor-pointer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                    >
                      <div className="relative flex flex-col items-center justify-end w-full">
                        {/* Bar */}
                        <motion.div
                          className={`w-8 mx-auto rounded-t-xl relative overflow-hidden shadow-lg ${
                            selectedChart === 'revenue' ? 'bg-gradient-to-t from-red-400 to-red-600' :
                            selectedChart === 'orders' ? 'bg-gradient-to-t from-blue-400 to-blue-600' :
                            'bg-gradient-to-t from-green-400 to-green-600'
                          } group-hover:shadow-xl transition-all duration-300`}
                          initial={{ height: 0 }}
                          animate={{ height: barHeight }}
                          transition={{ duration: 1.2, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                          whileHover={{ scaleX: 1.2, scaleY: 1.05 }}
                          style={{ minHeight: '8px' }}
                        >
                          {/* Shimmer Effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 animate-pulse"></div>
                          
                          {/* Value Display on Bar */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform rotate-90 whitespace-nowrap">
                              {data.label}
                            </span>
                          </div>
                        </motion.div>
                        
                        {/* Hover Tooltip */}
                        <motion.div
                          className="absolute -top-20 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-20 shadow-lg"
                          initial={{ y: 10, scale: 0.8 }}
                          whileHover={{ y: 0, scale: 1 }}
                        >
                          <div className="text-center">
                            <div className="font-bold">{data.label}</div>
                            <div className="text-xs opacity-80">{data.month}</div>
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                        </motion.div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              
              {/* Month Labels */}
              <div className="absolute bottom-0 left-0 right-0 h-12 flex items-center justify-between gap-2">
                {chartData[selectedChart].map((data, index) => (
                  <div key={index} className="flex-1 text-center">
                    <div className="text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-300">
                      {data.month}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Y-Axis Labels */}
              <div className="absolute left-0 top-0 bottom-16 w-12 flex flex-col-reverse justify-between text-xs text-gray-500">
                {[0, 0.25, 0.5, 0.75, 1].map((percentage) => {
                  const value = Math.round(maxValue * percentage);
                  return (
                    <div key={percentage} className="text-right pr-2">
                      {selectedChart === 'revenue' ? `₹${value}Cr` :
                       selectedChart === 'orders' ? `${value}K` :
                       `${value}K`}
                    </div>
                  );
                })}
              </div>
              
              {/* Grid Lines */}
              <div className="absolute left-12 right-0 top-0 bottom-16">
                {[0, 0.25, 0.5, 0.75, 1].map((percentage) => (
                  <div 
                    key={percentage}
                    className="absolute w-full border-t border-gray-200 opacity-30"
                    style={{ bottom: `${percentage * 100}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Detailed Metrics Grid */}
        {/* <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {detailedMetrics.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500 group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-gray-100 to-gray-200 group-hover:from-blue-100 group-hover:to-purple-100 transition-all duration-300">
                  <category.icon size={24} className="text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
                </div>
                <h4 className="text-xl font-bold text-gray-800 group-hover:text-gray-900 transition-colors duration-300">
                  {category.category}
                </h4>
              </div>
              
              <div className="space-y-6">
                {category.data.map((metric, metricIndex) => (
                  <motion.div 
                    key={metricIndex} 
                    className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 group-hover:bg-gray-100 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-1">{metric.label}</div>
                      <div className="text-2xl font-bold text-gray-800">{metric.value}</div>
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-bold px-3 py-2 rounded-full ${
                      metric.isPositive === true ? 'bg-green-100 text-green-700' : 
                      metric.isPositive === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {metric.isPositive === true ? <ArrowUp size={14} /> : 
                       metric.isPositive === false ? <ArrowDown size={14} /> : null}
                      {metric.trend}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div> */}

        {/* Enhanced Success Indicators */}
        <motion.div 
          className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-red-500 rounded-3xl p-12 text-white overflow-hidden"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-white/20 to-transparent animate-pulse"></div>
            <div className="absolute top-10 right-10 w-32 h-32 border-2 border-white/20 rounded-full animate-spin"></div>
            <div className="absolute bottom-10 left-10 w-24 h-24 border-2 border-white/20 rounded-full animate-bounce"></div>
          </div>
          
          <div className="relative z-10">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold mb-4">Platform Excellence Metrics</h3>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Key achievements that demonstrate our platform's transformative impact on vendor success
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: "🚀", title: "Growth Rate", value: "300%", subtitle: "Year over Year", description: "Vendor revenue growth" },
                { icon: "🏆", title: "Market Leader", value: "#1", subtitle: "Quick Commerce", description: "In vendor satisfaction" },
                { icon: "💝", title: "Vendor Rating", value: "4.8★", subtitle: "App Store Rating", description: "Consistently high rated" },
                { icon: "⚡", title: "Delivery Speed", value: "10min", subtitle: "Average Time", description: "Lightning fast delivery" }
              ].map((indicator, index) => (
                <motion.div
                  key={index}
                  className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 group cursor-pointer"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">{indicator.icon}</div>
                  <div className="text-3xl font-bold mb-2 group-hover:text-yellow-300 transition-colors duration-300">{indicator.value}</div>
                  <div className="text-lg font-semibold mb-1 opacity-90">{indicator.title}</div>
                  <div className="text-sm opacity-75 mb-2">{indicator.subtitle}</div>
                  <div className="text-xs opacity-60 group-hover:opacity-80 transition-opacity duration-300">{indicator.description}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VendorsAnalytics; 